import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { HttpError, StatisticsResponse } from "../../../api/types";
import useShowToast from "../../../hooks/useShowToast";

/**
 * Custom hook to fetch statistics from backend.
 * @param {number} year - Year to get statistics for
 * @returns {object} - Contains statistics data, loading state, error state, and refetch function.
 */
function useFetchStatistics(year: number) {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<StatisticsResponse | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HttpError | null>(null);
  const { errorToast } = useShowToast();

  const fetchStatistics = async (year: number) => {
    if (!year) {
      setStatistics(undefined);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.getStatistics(year);

      if (api.error) {
        setError(api.error);
        errorToast({
          title: t("club_management.statistics.fetch.error_title"),
          description: t("club_management.statistics.fetch.error_description"),
        });
      }

      setStatistics(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics(year);
  }, [year]);

  return {
    statistics,
    loading,
    error,
    refetchStatistics: fetchStatistics,
  };
}

export default useFetchStatistics;
